import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import config from '../../config';
import * as XLSX from 'xlsx';
import FailedMessage from './FailedMessage';

// ─── Spinner helpers (same pattern as Form4) ─────────────────────────────────
const spinnerStyle = {
  border: '4px solid rgba(255,255,255,0.3)',
  borderTop: '4px solid #3498db',
  borderRadius: '50%',
  width: '24px',
  height: '24px',
  animation: 'spin 1s linear infinite',
  marginRight: '8px',
};
const loadingTextStyle = { display: 'flex', alignItems: 'center' };

const PAGE_LIMIT = 5;

const ManagerMapping = () => {
  // ── Data & pagination ────────────────────────────────────────────────────────
  const [data, setData]               = useState([]);
  const [totalPages, setTotalPages]   = useState(0);
  const [totalCount, setTotalCount]   = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // ── Filters ──────────────────────────────────────────────────────────────────
  const [selection, setSelection]     = useState('');   // slab filter (days param)

  // ── Agency lists ─────────────────────────────────────────────────────────────
  const [agency, setAgency]           = useState([]);
  const [ID, setID]                   = useState('');           // selected agency id
  const [newAgencyID, setNewAgencyID] = useState('');           // replacement agency id

  // ── Loading states ───────────────────────────────────────────────────────────
  const [loading, setLoading]             = useState(false);
  const [loadingbutton, setloadingbutton] = useState(false);
  const [mappingLoading, setMappingLoading]   = useState(false);
  const [unmappingloading, setUnmappingloading] = useState(false);
  const [replaceloading, setReplaceLoading]   = useState(false);
  const [uploadExcel, setuploadExcel]         = useState(false);

  // ── Messages ─────────────────────────────────────────────────────────────────
  const [message, setMessage]         = useState('');
  const [UploadMessage, setUploadMessage] = useState('');
  const [error, setError]             = useState(null);

  // ── Refs / misc ───────────────────────────────────────────────────────────────
  const modalRef      = useRef(null);
  const individual    = useSelector((state) => state.Individual.individual_role);
  const user_id       = useSelector((state) => state.user.currentUser);

  // Inject keyframe once
  useEffect(() => {
    try {
      const ss = document.styleSheets[0];
      ss.insertRule(
        '@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}',
        ss.cssRules.length
      );
    } catch (_) {}
  }, []);

  const handleHideMessage = () => setMessage('');

  // ─────────────────────────────────────────────────────────────────────────────
  // 1. FETCH TABLE DATA  →  /subAdmin?pages=&days=
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchData = async (page = currentPage) => {
    setLoading(true);
    const token = localStorage.getItem('access_token');
    try {
      const params = new URLSearchParams();
      params.set('pages', page);
      if (selection) params.set('days', selection);

      const res  = await fetch(`${config.apiUrlBetaV1}subAdmin?${params.toString()}`, {
        method: 'GET',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
      });
      const json = await res.json();

      if (json.status) {
        setData(json.data || []);
        setTotalPages(json.totalPages || 0);
        setTotalCount(json.totalLoansCount || 0);
      } else {
        setData([]);
        setTotalPages(0);
        setTotalCount(0);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 2. FETCH AGENCIES  →  /getAllAgencies
  const fetchAgencies = async () => {
    const token = localStorage.getItem('access_token');
    try {
      const res  = await fetch(`${config.apiUrlBetaV2}getAllAgencies`, {
        method: 'GET',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      setAgency(json.data || []);
    } catch (_) {}
  };

  useEffect(() => {
    fetchData(currentPage);
    fetchAgencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selection, ID]);

  // ─────────────────────────────────────────────────────────────────────────────
  // PAGINATION
  // ─────────────────────────────────────────────────────────────────────────────
  const handlePageClick = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const left  = Math.max(1, currentPage - Math.floor(PAGE_LIMIT / 2));
    const right = Math.min(totalPages, currentPage + Math.floor(PAGE_LIMIT / 2));

    if (left > 1) { pages.push(1); if (left > 2) pages.push('...'); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push('...'); pages.push(totalPages); }
    return pages;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // SLAB TOTAL COUNT  →  /slab_totalLoans
  // ─────────────────────────────────────────────────────────────────────────────
  const handletotal = async () => {
    const token = localStorage.getItem('access_token');
    try {
      const res  = await fetch(`${config.apiUrlBetaV2}slab_totalLoans?days=${selection}`, {
        method: 'GET',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      setTotalCount(json.total_document || 0);
    } catch (_) {}
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // AUTO MAP  →  /updatecollections  (same pagination loop as Form4)
  // ─────────────────────────────────────────────────────────────────────────────
  let currentPageRef = 1;
  let totalPageRef   = 0;

  const handlemapped = async (page) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(
      `${config.apiUrlBetaV2}updatecollections?days=${selection}&page=${page}`,
      {
        method: 'PUT',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: ID }),
      }
    );
    const json = await res.json();
    totalPageRef = json.totalPages || 0;
  };

  const fetchAllPages = async (e) => {
    e.preventDefault();
    setMappingLoading(true);
    currentPageRef = 1;
    await handlemapped(currentPageRef);

    while (currentPageRef < totalPageRef) {
      currentPageRef++;
      await handlemapped(currentPageRef);
    }

    setMappingLoading(false);
    setMessage('All data mapped successfully');
    window.location.reload();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // UN-MAP  →  /unmapping_loans_agency
  // ─────────────────────────────────────────────────────────────────────────────
  const unmapping = async (e) => {
    e.preventDefault();
    setUnmappingloading(true);
    const type  = e.target.innerText === 'Replace' ? 'Replace' : 'Unmap';
    const token = localStorage.getItem('access_token');
    try {
      const res  = await fetch(
        `${config.apiUrlBetaV2}unmapping_loans_agency?days=${selection}&type=${type}&id=${ID}`,
        {
          method: 'GET',
          headers: { Authorization: token, 'Content-Type': 'application/json' },
        }
      );
      const json = await res.json();
      if (json.data?.acknowledged === true) {
        setMessage('All data unmapped successfully');
        window.location.reload();
      }
    } catch (_) {
    } finally {
      setUnmappingloading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // REPLACE AGENCY  →  /replaceAgency
  // ─────────────────────────────────────────────────────────────────────────────
  const handleReplace = async (e) => {
    e.preventDefault();
    setReplaceLoading(true);
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(`${config.apiUrlBetaV2}replaceAgency`, {
        method: 'PUT',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldAgencyId: ID, newAgencyId: newAgencyID, days: selection }),
      });
      if (res.ok) setMessage('Agency replaced successfully');
    } catch (_) {
    } finally {
      setReplaceLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // UPLOAD EXCEL  →  /upload_loans  (chunked, same as Form4)
  // ─────────────────────────────────────────────────────────────────────────────
  const convertExcelToJson = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const wb   = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        resolve(XLSX.utils.sheet_to_json(ws));
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

const handleFileChange = async (event) => {
  event.preventDefault();
  setuploadExcel(true);
  const token        = localStorage.getItem('access_token');
  const selectedFile = event.target.files[0];

  let jsonData = await convertExcelToJson(selectedFile);

  // ✅ Now includes MappedAdmin
  jsonData = jsonData.map((item) => ({
    loan_id:      item?.loan_id?.toString().trim(),
    MappedAdmin:  item?.MappedAdmin?.toString().trim(),   // ✅ was missing
    MappedAgency: item?.MappedAgency?.toString().trim(),
  }));

  const chunkSize = 100;
  const chunks    = [];
  for (let i = 0; i < jsonData.length; i += chunkSize) {
    chunks.push(jsonData.slice(i, i + chunkSize));
  }

  let success = true;
  for (let ci = 0; ci < chunks.length; ci++) {
    try {
      const res = await fetch(`${config.apiUrlBetaV1}upload_loans`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: chunks[ci] }),
      });
      if (!res.ok) { success = false; break; }
    } catch (_) {
      success = false;
      break;
    }
  }

  setuploadExcel(false);
  if (success) {
    setUploadMessage('All data uploaded successfully');
    window.location.reload();
  } else {
    setUploadMessage('Unable to upload data');
  }
};

  // ─────────────────────────────────────────────────────────────────────────────
  // EXCEL EXPORT  →  /subAdmin?excel=true  (all pages, same as Form4)
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchDataForPage = async (page) => {
    const token  = localStorage.getItem('access_token');
    const params = new URLSearchParams();
    params.set('pages', page);
    params.set('excel', 'true');
    if (selection) params.set('days', selection);

    const res = await fetch(`${config.apiUrlBetaV1}subAdmin?${params.toString()}`, {
      headers: { Authorization: token, 'Content-Type': 'application/json' },
    });
    return res.json();
  };

  const handleExport = async () => {
    setloadingbutton(true);
    try {
      const first = await fetchDataForPage(1);
      let allData = first.data || [];
      const pages = first.totalPages || 1;

      for (let p = 2; p <= pages; p++) {
        const next = await fetchDataForPage(p);
        if (next.data) allData = [...allData, ...next.data];
      }

      const ws = XLSX.utils.json_to_sheet(allData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      XLSX.writeFile(wb, 'subadmin_loans.xlsx');
    } catch (_) {
      setError('Error exporting data');
    } finally {
      setloadingbutton(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Loading overlay ────────────────────────────────────────────────── */}
      {loading ? (
        <span style={loadingTextStyle}>
          <div style={spinnerStyle} />
          <span>Loading…</span>
        </span>
      ) : (
        <>
          {/* ── Toolbar ──────────────────────────────────────────────────────── */}
          <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">

            {/* Left controls */}
            {/* <div className="d-flex flex-wrap align-items-center gap-3">

              <select
                id="status"
                className="form-select form-select-sm w-auto"
                value={selection}
                onChange={(e) => { setSelection(e.target.value); setCurrentPage(1); }}
              >
                <option value="" disabled>Select defaulted slab</option>
                <option value="1-30">1–30 days</option>
                <option value="31-60">31–60 days</option>
                <option value="61-90">61–90 days</option>
                <option value="91-120">91–120 days</option>
                <option value="121-180">121–180 days</option>
                <option value="181-365">181–365 days</option>
                <option value="365">365+ days</option>
                <option value="All">All</option>
              </select>

              <select
                id="Agency"
                className="form-select form-select-sm w-auto"
                value={ID}
                onChange={(e) => setID(e.target.value)}
              >
                <option value="" disabled>Select Agency</option>
                {agency?.map((a, i) => (
                  <option key={i} value={a._id}>{a.username}</option>
                ))}
              </select>

              <button
                className="btn-primary rounded-2 px-6 py-8 ml-2"
                style={{ backgroundColor: '#007bff', color: 'white' }}
                data-bs-toggle="modal"
                data-bs-target="#Modal1"
                onClick={handletotal}
                disabled={!ID || !selection}
              >
                Auto Map
              </button>

              <button
                className="btn-primary rounded-2 px-6 py-8 ml-2"
                style={{ backgroundColor: '#007bff', color: 'white' }}
                data-bs-toggle="modal"
                data-bs-target="#Modal2"
                onClick={handletotal}
              >
                UN-Map
              </button>
            </div> */}

            <div className="d-flex flex-wrap align-items-center gap-3">

              {/* Download Excel */}
              {individual?.loan_details?.export_access === true && (
                <button className="btn btn-sm btn-primary-600" onClick={handleExport}>
                  {loadingbutton ? (
                    <span style={loadingTextStyle}>
                      <div style={spinnerStyle} />
                      downloading…
                    </span>
                  ) : (
                    <><i className="ri-add-line" /> Download Excel</>
                  )}
                </button>
              )}

              {/* Upload Excel → /upload_loans */}
              {uploadExcel ? (
                <span style={loadingTextStyle}>
                  <div style={spinnerStyle} />
                  uploading…
                </span>
              ) : (
                <label
                  htmlFor="file-upload-name"
                  className="mb-14 border border-neutral-600 fw-medium text-secondary-light px-16 py-10 radius-12 d-inline-flex align-items-center gap-1 bg-hover-neutral-200"
                  style={{ cursor: 'pointer' }}
                >
                  <Icon icon="solar:upload-linear" className="text-xl" />
                  Upload xlsx map
                  <input
                    type="file"
                    id="file-upload-name"
                    accept=".xlsx,.xls"
                    hidden
                    onChange={handleFileChange}
                  />
                </label>
              )}

              {UploadMessage && (
                <FailedMessage message={UploadMessage} onHide={() => setUploadMessage('')} />
              )}
            </div>
          </div>

          {/* ── Error banner ────────────────────────────────────────────────── */}
          {error && (
            <div className="alert alert-danger mx-3 mt-2" role="alert">{error}</div>
          )}

          {/* ── Table ───────────────────────────────────────────────────────── */}
          <div className="card-body">
            <div style={{ overflowX: 'auto', maxHeight: '750px', overflowY: 'auto' }}>
              <table className="table bordered-table mb-0">
                <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }}>
                  <tr>
                    <th>S.NO</th>
                    <th>Loan Date</th>
                    <th>Loan Id</th>
                    <th>Agency Name</th>
                    <th>Name</th>
                    <th>Account Type</th>
                    <th>Mobile Number</th>
                    <th>College Name</th>
                    <th>Loan Amount</th>
                    <th>DPD Days</th>
                    <th>Daily Late Fee</th>
                    <th>Service Charge</th>
                    <th>Total Penalty</th>
                    <th>Amount Payable</th>
                    <th>Due Date</th>
                    <th>Address</th>
                    <th>State</th>
                    <th>Email</th>
                    <th>Pan Number</th>
                    <th>20%</th>
                    <th>40%</th>
                    <th>50%</th>
                    <th>60%</th>
                    <th>70%</th>
                    <th>90%</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={25} className="text-center py-4 text-muted">
                        No loans found.
                      </td>
                    </tr>
                  ) : (
                    data.map((row, index) => {
                      const p      = row.percentage || {};
                      const offer  = (key) => p[key]?.waived_total_amount ?? '—';

                      return (
                        <tr key={index}>
                          {/* S.NO */}
                          <td className="text-danger-600">
                            {(currentPage - 1) * 10 + index + 1}
                          </td>

                          {/* Loan Date */}
                          <td>
                            {row.start && !isNaN(new Date(row.start))
                              ? new Date(row.start).toISOString().split('T')[0]
                              : 'N/A'}
                          </td>

                          {/* Loan Id */}
                          <td>
                            <div className="form-check style-check d-flex align-items-center">
                              <label className="form-check-label">{row.loan_id}</label>
                            </div>
                          </td>

                          {/* Agency Name */}
                          <td>
                            <Link to="#" className="text-primary-600">
                              {row.Agency ?? 'NA'}
                            </Link>
                          </td>

                          {/* Name */}
                          <td>
                            <h6 className="text-md mb-0 fw-medium flex-grow-1">
                              {row.Name ?? '—'}
                            </h6>
                          </td>

                          {/* Account Type */}
                          <td>{row.user_acc_type ?? '—'}</td>

                          {/* Mobile */}
                          <td>{row.Mobile ?? '—'}</td>

                          {/* College */}
                          <td>{row.college_name ?? '—'}</td>

                          {/* Loan Amount */}
                          <td>{row.loan_amt}</td>

                          {/* DPD */}
                          <td>{row.defaulted ?? '—'}</td>

                          {/* Daily Late Fee */}
                          <td>{row.dailyLateFee ?? 0}</td>

                          {/* Service Charge */}
                          <td>{row.serviceChargeFee ?? 0}</td>

                          {/* Total Penalty */}
                          <td>{row.totalPenalty ?? 0}</td>

                          {/* Amount Payable */}
                          <td>{row.totalAmountPayable}</td>

                          {/* Due Date */}
                          <td>
                            {row.due && !isNaN(new Date(row.due))
                              ? new Date(row.due).toISOString().split('T')[0]
                              : 'N/A'}
                          </td>

                          {/* Address */}
                          <td>{row.Address ?? '—'}</td>

                          {/* State */}
                          <td>{row.state_code ?? '—'}</td>

                          {/* Email */}
                          <td>{row.Email ?? '—'}</td>

                          {/* Pan */}
                          <td>{row.pan_number ?? '—'}</td>

                          {/* Offers — keys match what subAdmin backend returns */}
                          <td>{offer('20%')}</td>
                          <td>{offer('40%')}</td>
                          <td>{offer('50%')}</td>
                          <td>{offer('60%')}</td>
                          <td>{offer('70%')}</td>
                          <td>{offer('90%')}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ─────────────────────────────────────────────────── */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-24">
              <span>
                {totalCount > 0
                  ? `Showing ${(currentPage - 1) * 10 + 1}–${Math.min(currentPage * 10, totalCount)} of ${totalCount} entries`
                  : 'No entries'}
              </span>

              <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                {/* Prev */}
                <li className="page-item">
                  <Link
                    className={`page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px me-8 w-32-px ${currentPage === 1 ? 'bg-base' : ''}`}
                    to="#"
                    onClick={() => handlePageClick(currentPage - 1)}
                  >
                    <Icon icon="ep:d-arrow-left" className="text-xl" />
                  </Link>
                </li>

                {getPageNumbers().map((pg, idx) => (
                  <li key={idx} className="page-item">
                    <Link
                      className={`page-link fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px me-8 w-32-px ${pg === currentPage ? 'bg-primary-600 text-white' : 'bg-primary-50 text-secondary-light'}`}
                      to="#"
                      onClick={() => pg !== '...' && handlePageClick(pg)}
                    >
                      {pg}
                    </Link>
                  </li>
                ))}

                {/* Next */}
                <li className="page-item">
                  <Link
                    className={`page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px me-8 w-32-px ${currentPage === totalPages ? 'bg-base' : ''}`}
                    to="#"
                    onClick={() => handlePageClick(currentPage + 1)}
                  >
                    <Icon icon="ep:d-arrow-right" className="text-xl" />
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          MODAL 1 — Auto Map confirmation
      ═══════════════════════════════════════════════════════════════════════ */}
      <div
        className="modal fade"
        id="Modal1"
        tabIndex={-1}
        aria-labelledby="Modal1Label"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="Modal1Label">Auto Map</h1>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>

            {message && <FailedMessage message={message} onHide={handleHideMessage} />}

            <div className="modal-body p-24">
              <div className="row">
                <div className="d-flex align-items-center justify-content-between border border-red-300 bg-red-50 p-2 rounded shadow-sm">
                  <p className="fw-semibold text-gray-500">
                    Are you sure you want to map the data?
                  </p>
                  <p className="text-right text-gray-600">{totalCount}</p>
                </div>

                <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                  <button
                    type="reset"
                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary border border-primary-600 text-md px-48 py-12 radius-8"
                    onClick={fetchAllPages}
                    disabled={mappingLoading}
                  >
                    {mappingLoading ? (
                      <span style={loadingTextStyle}>
                        <div style={spinnerStyle} />
                        Mapping…
                      </span>
                    ) : 'Mapping'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          MODAL 2 — UN-Map / Replace
      ═══════════════════════════════════════════════════════════════════════ */}
      <div
        className="modal fade"
        id="Modal2"
        tabIndex={-1}
        aria-labelledby="Modal2Label"
        aria-hidden="true"
        ref={modalRef}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="Modal2Label">Un-Map / Replace</h1>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>

            <div className="modal-body p-24">
              <div className="row">

                {/* Count warning */}
                <div className="d-flex align-items-center justify-content-between border border-red-300 bg-red-50 p-2 rounded shadow-sm mb-3">
                  <p className="fw-semibold text-gray-500">Are you sure you want to un-map the data?</p>
                  <p className="text-right text-gray-600">{totalCount}</p>
                </div>

                {/* Slab re-select */}
                <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                  <select
                    id="status2"
                    className="form-select form-select-sm w-auto"
                    value={selection}
                    onChange={(e) => { setSelection(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="" disabled>Select defaulted slab</option>
                    <option value="1-30">1–30 days</option>
                    <option value="31-60">31–60 days</option>
                    <option value="61-90">61–90 days</option>
                    <option value="91-120">91–120 days</option>
                    <option value="121-180">121–180 days</option>
                    <option value="181-365">181–365 days</option>
                    <option value="365">365+ days</option>
                    <option value="All">All</option>
                  </select>
                </div>

                {/* Agency select */}
                <div className="mt-2 mb-3">
                  <label className="fw-semibold text-gray-700">Select Agency:</label>
                  <select
                    className="form-select form-select-sm w-auto mt-1"
                    value={ID}
                    onChange={(e) => setID(e.target.value)}
                  >
                    <option value="" disabled>Select Agency</option>
                    {agency?.map((a, i) => (
                      <option key={i} value={a._id}>{a.username}</option>
                    ))}
                  </select>
                </div>

                {/* Replace with another agency */}
                <div className="mt-2 mb-3">
                  <label className="fw-semibold text-gray-700">Replace with Another Agency:</label>
                  <select
                    className="form-select mt-1"
                    value={newAgencyID}
                    onChange={(e) => setNewAgencyID(e.target.value)}
                  >
                    <option value="" disabled>Select New Agency</option>
                    {agency?.map((a, i) => (
                      <option key={i} value={a._id}>{a.username}</option>
                    ))}
                  </select>
                </div>

                {message && <FailedMessage message={message} onHide={handleHideMessage} />}

                {/* Action buttons */}
                <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                  <button
                    type="reset"
                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    Cancel
                  </button>

                  {/* UN-MAP */}
                  <button
                    type="button"
                    className="border border-warning-600 bg-hover-warning-200 text-warning-600 text-md px-40 py-11 radius-8"
                    onClick={unmapping}
                    disabled={unmappingloading}
                  >
                    {unmappingloading ? (
                      <span style={loadingTextStyle}>
                        <div style={spinnerStyle} />
                        UN-MAP…
                      </span>
                    ) : 'UN-MAP'}
                  </button>

                  {/* REPLACE */}
                  <button
                    type="button"
                    className="border border-warning-600 bg-hover-warning-200 text-warning-600 text-md px-40 py-11 radius-8"
                    onClick={handleReplace}
                    disabled={replaceloading}
                  >
                    {replaceloading ? (
                      <span style={loadingTextStyle}>
                        <div style={spinnerStyle} />
                        Replace…
                      </span>
                    ) : 'Replace'}
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManagerMapping;